import { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../common/Header';
import Footer from '../common/Footer';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import EmojiPeopleRoundedIcon from '@mui/icons-material/EmojiPeopleRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import AdjustRoundedIcon from '@mui/icons-material/AdjustRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import ArchitectureRoundedIcon from '@mui/icons-material/ArchitectureRounded';
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded';
import { CircularProgress } from '@mui/material';
import SeoHelmet from '../common/SeoHelmet';

const About = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get('/api/v1/users/getAboutMetrics', {
          signal: ac.signal, // axios v1 supports AbortController
          headers: { Accept: 'application/json' },
        });
        setData(res.data);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') setError(e);
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, []);

  return (
    <>
      <SeoHelmet
        title="GeoCalc | About"
        description="What GeoCalc.app is, who it serves, and platform metrics and roadmap. Calculation tools for geotechnical engineering focused on speed, consistency, and reliability."
        url="https://www.geocalc.app/about"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'About', url: 'https://www.geocalc.app/about' },
        ]}
      />

      <Header />
      <div className="aboutUs">
        <h1>About GeoCalc</h1>
        <div className="whatIsGeoCalc">
          <div className="whatIsGeoCalcBox">
            <div className="whatIsGeoCalcBoxIcon">
              <CalculateRoundedIcon fontSize="large" />
            </div>
            <div className="whatIsGeoCalcBoxHeader">What is Geocalc.app?</div>
            <div className="whatIsGeoCalcBoxText">
              GeoCalc.app is a web platform that delivers calculation tools for geotechnical engineering. It is designed to make
              routine analyses faster, reduce errors, and present results in a clear, consistent format. The focus is on
              practicality and reliability, helping engineers move efficiently from input to decision.
            </div>
          </div>
          <div className="whatIsGeoCalcBox">
            <div className="whatIsGeoCalcBoxIcon">
              <EmojiPeopleRoundedIcon fontSize="large" />
            </div>
            <div className="whatIsGeoCalcBoxHeader">Who is GeoCalc.app for?</div>
            <div className="whatIsGeoCalcBoxText">
              GeoCalc.app is built for professionals and learners in geotechnical engineering. Whether used in design offices,
              research, or education, it provides straightforward tools that support accurate analysis and clear understanding.
              The platform is meant for anyone who values efficiency and consistency in geotechnical work.
            </div>
          </div>
          <div className="whatIsGeoCalcBox">
            <div className="whatIsGeoCalcBoxIcon">
              <CheckCircleOutlineRoundedIcon fontSize="large" />
            </div>
            <div className="whatIsGeoCalcBoxHeader">Why use GeoCalc.app?</div>
            <div className="whatIsGeoCalcBoxText">
              GeoCalc.app is designed to remove the friction of manual spreadsheets and scattered resources. It keeps calculations
              in one place, accessible anytime, and ensures results are delivered in a consistent and dependable format.
            </div>
          </div>
        </div>
        <h1>By the Numbers</h1>
        <p className="AboutNumbersUpdateDateText">As of {loading ? <CircularProgress size={'10px'} /> : data.asOfISO}</p>
        <p>“Without data, you’re just another person with an opinion.” — W. Edwards Deming</p>
        <div className="howWeDoing">
          <div className="howWeDoingBox">
            <AccountCircleRoundedIcon fontSize="medium" color="primary" className="howWeDoingBoxIcons" />
            <div className="howWeDoingBoxNumber">{loading ? <CircularProgress /> : data.users}</div>
            <div className="howWeDoingBoxText">Users</div>
          </div>
          <div className="howWeDoingBox">
            <CalculateRoundedIcon fontSize="medium" color="primary" className="howWeDoingBoxIcons" />
            <div className="howWeDoingBoxNumber">{loading ? <CircularProgress /> : data.calculations}</div>
            <div className="howWeDoingBoxText">Calculations performed</div>
          </div>
          <div className="howWeDoingBox">
            <ArchitectureRoundedIcon fontSize="medium" color="primary" className="howWeDoingBoxIcons" />

            <div className="howWeDoingBoxNumber">{loading ? <CircularProgress /> : data.tools}</div>
            <div className="howWeDoingBoxText">Calculations tools</div>
          </div>
          <div className="howWeDoingBox">
            <SystemUpdateAltRoundedIcon fontSize="medium" color="primary" className="howWeDoingBoxIcons" />

            <div className="howWeDoingBoxNumber">{loading ? <CircularProgress /> : data.updates}</div>
            <div className="howWeDoingBoxText">Updates shipped</div>
          </div>
        </div>
        <h1>Roadmap</h1>
        <div className="aboutRoadMapBox">
          GeoCalc.app is continuously improving. We release regular updates, refine existing tools, and add new ones based on the
          needs of engineers and feedback from the community. The goal is to keep the platform practical, reliable, and aligned
          with real-world geotechnical practice.
          <p>
            <AdjustRoundedIcon fontSize="small" /> PDF exports
          </p>
          <p>
            <AdjustRoundedIcon fontSize="small" /> Calculation details
          </p>
          <p>
            <AdjustRoundedIcon fontSize="small" /> More geotechnical calculation modules
          </p>
          <p>
            <AdjustRoundedIcon fontSize="small" />
            Usability improvements
          </p>
          <p>
            <AdjustRoundedIcon fontSize="small" />
            Faster performance and stability updates
          </p>
          <p>
            <AdjustRoundedIcon fontSize="small" />
            Expanded references and documentation
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default About;
